"use client"
import React, { useEffect, useState } from "react"
import Navbar from "../../components/account/Navbar"
import Footer from "../../components/account/Footer"
import { useAuth } from "../../hooks/useAuth"
import { userAPI } from "../../services/api"
import { useTranslation } from "react-i18next"

const BlockedUsers = () => {
  const { t, i18n } = useTranslation()
  const [blockedUsers, setBlockedUsers] = useState([])
  const [pagination, setPagination] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)
  const [userToUnblock, setUserToUnblock] = useState(null)
  const [showUnblockModal, setShowUnblockModal] = useState(false)

  const { user: currentUser } = useAuth()

  const fetchBlockedUsers = async (page = 1) => {
    try {
      setError(null)
      setLoading(page === 1)
      setLoadingMore(page > 1)

      const response = await userAPI.getBlockedUsers({
        page,
        per_page: 15,
      })

      const payload = response.data || {}
      const users = payload.data || []

      setBlockedUsers((prev) =>
        page === 1 ? users : [...prev, ...users]
      )

      setPagination(payload.pagination || null)
    } catch (err) {
      console.error("Failed to load blocked users", err)
      setError(t("blockedUsers.errors.load"))
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }

  useEffect(() => {
    fetchBlockedUsers(1)
  }, [])

  const unblockUser = async () => {
    if (!userToUnblock || !userToUnblock.user_id) return
    try {
      await userAPI.unblockUser(userToUnblock.user_id)
      setBlockedUsers((prev) => prev.filter((user) => user.user_id !== userToUnblock.user_id))
      setSuccessMessage(t("blockedUsers.messages.unblocked"))
      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (err) {
      console.error("Failed to unblock user", err)
      setError(t("blockedUsers.errors.unblock"))
      setTimeout(() => setError(null), 5000)
    } finally {
      setShowUnblockModal(false)
      setUserToUnblock(null)
    }
  }

  const confirmUnblock = (user) => {
    setUserToUnblock(user)
    setShowUnblockModal(true)
  }

  const loadMore = () => {
    if (!pagination) return
    if (pagination.current_page >= pagination.last_page) return
    fetchBlockedUsers(pagination.current_page + 1)
  }

  return (
    <div
      className={`blocked-users-root ${i18n.dir() === "rtl" ? "rtl" : ""}`}
      dir={i18n.dir()}
      lang={i18n.language}
    >
      <Navbar currentUser={currentUser} userType={currentUser?.user_type} />
      <main
        className="blocked-users-main"
        style={{
          marginTop: "5%",
          minHeight: "calc(100vh - 160px)",
          padding: "48px 32px",
          background: "#F9FAFB",
        }}
      >
        <section
          style={{
            maxWidth: "960px",
            margin: "0 auto",
            background: "#FFFFFF",
            borderRadius: "24px",
            padding: "32px",
            boxShadow: "0 15px 40px rgba(15, 23, 42, 0.15)",
          }}
        >
          <header
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "24px",
              flexWrap: "wrap",
              gap: "12px",
            }}
          >
            <div>
              <h1
                style={{
                  margin: 0,
                  fontSize: "24px",
                  fontFamily: '"Red Hat Display", sans-serif',
                  color: "#111827",
                }}
              >
                {t("blockedUsers.title")}
              </h1>
              <p
                style={{
                  margin: 0,
                  fontSize: "14px",
                  color: "#6B7280",
                }}
              >
                {t("blockedUsers.loading")}
              </p>
            </div>
            {successMessage && (
              <div
                style={{
                  padding: "8px 16px",
                  borderRadius: "999px",
                  background: "#ECFCCB",
                  color: "#15803D",
                  fontSize: "14px",
                }}
              >
                {successMessage}
              </div>
            )}
          </header>

          {error && (
            <div
              style={{
                marginBottom: "16px",
                padding: "12px 16px",
                borderRadius: "12px",
                background: "#FEF2F2",
                color: "#B91C1C",
                fontSize: "14px",
              }}
            >
              {error}
            </div>
          )}

          {loading && blockedUsers.length === 0 ? (
            <div
              style={{
                color: "#6B7280",
                textAlign: "center",
                padding: "32px 0",
              }}
            >
              {t("blockedUsers.loading")}
            </div>
          ) : (
            <>
              {blockedUsers.length === 0 ? (
                <div
                  style={{
                    color: "#6B7280",
                    textAlign: "center",
                    padding: "32px 0",
                  }}
                >
                  {t("blockedUsers.empty")}
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                  }}
                >
                  {blockedUsers.map((user) => (
                    <div
                      key={user.user_id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "16px",
                        borderRadius: "16px",
                        background: "#F8FAFC",
                        justifyContent: "space-between",
                        gap: "16px",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <div
                          style={{
                            width: "48px",
                            height: "48px",
                            borderRadius: "50%",
                            background: "#E5E7EB",
                            overflow: "hidden",
                          }}
                        >
                          <img
                            src={user.profile_image_url || "/icons/person.png"}
                            alt={user.full_name || user.username || "Blocked user"}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        </div>
                        <div>
                          <div
                            style={{
                              fontFamily: '"Red Hat Display", sans-serif',
                              fontWeight: 600,
                              color: "#111827",
                            }}
                          >
                            {user.full_name || `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.username}

                            {/* <div style={{ fontSize: "13px", color: "#6B7280" }}>
                            @{user.username}
                          </div> */}
                          </div>
                        </div>

                      </div>
                      <button
                        onClick={() => confirmUnblock(user)}
                        style={{
                          background: "blue",
                          border: "none",
                          color: "#FFFFFF",
                          padding: "8px 18px",
                          borderRadius: "999px",
                          cursor: "pointer",
                          fontFamily: '"Red Hat Display", sans-serif',
                          fontWeight: 600,
                        }}
                      >
                        {t("blockedUsers.unblock")}
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {pagination &&
                pagination.current_page < pagination.last_page && (
                  <div
                    style={{
                      marginTop: "24px",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <button
                      onClick={loadMore}
                      disabled={loadingMore}
                      style={{
                        padding: "10px 22px",
                        borderRadius: "999px",
                        border: "none",
                        background: "#1D4ED8",
                        color: "#FFFFFF",
                        fontFamily: '"Red Hat Display", sans-serif',
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      {loadingMore ? t("blockedUsers.loading") : t("blockedUsers.loadMore")}
                    </button>
                  </div>
                )}
            </>
          )}
        </section>
      </main>
      <Footer />
      {showUnblockModal && userToUnblock && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "20px",
          }}
          onClick={() => {
            setShowUnblockModal(false)
            setUserToUnblock(null)
          }}
        >
          <div
            style={{
              background: "#FFFFFF",
              borderRadius: "16px",
              padding: "32px",
              maxWidth: "400px",
              width: "100%",
              textAlign: "center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                width: "64px",
                height: "64px",
                margin: "0 auto 16px auto",
                background: "linear-gradient(135deg, rgba(244, 114, 182, 0.1), rgba(239, 68, 68, 0.12))",
                borderRadius: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 12px 24px rgba(14, 165, 233, 0.12)",
              }}
            >
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                <defs>
                  <linearGradient id="unblockGradient" x1="0" x2="1" y1="0" y2="1">
                    <stop offset="0%" stopColor="#EF4444" />
                    <stop offset="100%" stopColor="#F97316" />
                  </linearGradient>
                </defs>
                <path
                  d="M16 2C8.268 2 2 8.268 2 16s6.268 14 14 14 14-6.268 14-14S23.732 2 16 2zm5.707 17.293-1.414 1.414L16 17.414l-4.293 4.293-1.414-1.414L14.586 16 10.293 11.707l1.414-1.414L16 14.586l4.293-4.293 1.414 1.414L17.414 16l4.293 3.293z"
                  fill="url(#unblockGradient)"
                />
                <path
                  d="M21.293 10.707a1 1 0 0 0-1.414-1.414L11 18.172V21h2.828l8.465-8.293z"
                  fill="#fff"
                  opacity="0.9"
                />
              </svg>
            </div>
            <h3
              style={{
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: "20px",
                fontWeight: 600,
                color: "#303E4E",
                margin: "0 0 8px 0",
              }}
            >
              {t("blockedUsers.modals.unblockTitle", {
                name: userToUnblock.full_name || userToUnblock.username,
              })}
            </h3>
            <p
              style={{
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: "16px",
                color: "#8B8B8B",
                margin: "0 0 24px 0",
                lineHeight: "1.5",
              }}
            >
              {t("blockedUsers.modals.unblockDescription")}
            </p>
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "center",
              }}
            >
              <button
                onClick={() => {
                  setShowUnblockModal(false)
                  setUserToUnblock(null)
                }}
                style={{
                  padding: "12px 24px",
                  background: "#F3F4F6",
                  color: "#6B7280",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                {t("blockedUsers.modals.cancel")}
              </button>
              <button
                onClick={unblockUser}
                style={{
                  padding: "12px 24px",
                  background: "#10B981",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                {t("blockedUsers.modals.confirmUnblock")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default BlockedUsers

